import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, fmtOdds, fmtPct, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ProGate } from "@/components/ProGate";
import { TeamLogo } from "@/components/TeamLogo";
import { LeagueChip } from "@/components/LeagueLogo";
import {
  Layers3, X, Loader2, Sparkles, TrendingUp, Save, RotateCcw, Calendar,
} from "lucide-react";
import { toast } from "sonner";

// Markets available per game card
const MARKETS = [
  { group: "Moneyline", sideHome: { market: "moneyline", selection: "home" }, sideAway: { market: "moneyline", selection: "away" } },
  { group: "Spread",    sideHome: { market: "spread", selection: "home" },    sideAway: { market: "spread", selection: "away" } },
  { group: "Total",     sideHome: { market: "total", selection: "over" },     sideAway: { market: "total", selection: "under" }, labelHome: "Over", labelAway: "Under" },
];

const legKey = (l) => `${l.game_id}:${l.market}:${l.selection}`;

export default function Parlay() {
  return (
    <ProGate feature="Parlay Builder">
      <ParlayInner />
    </ProGate>
  );
}

function ParlayInner() {
  const [games, setGames] = useState([]);
  const [slip, setSlip] = useState([]); // array of {game_id, market, selection}
  const [preview, setPreview] = useState(null);
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stake, setStake] = useState(25);

  useEffect(() => {
    api.get("/games").then((r) => setGames(r.data));
  }, []);

  const selectedSet = useMemo(() => new Set(slip.map(legKey)), [slip]);

  const toggleLeg = (leg) => {
    setSlip((cur) => {
      const k = legKey(leg);
      // Only one selection per game/market group allowed (e.g. cant pick both home AND away ML)
      const filtered = cur.filter(
        (l) => !(l.game_id === leg.game_id && l.market === leg.market)
      );
      if (filtered.length !== cur.length && cur.some((l) => legKey(l) === k)) {
        return filtered;
      }
      if (filtered.length >= 12) {
        toast.error("Max 12 legs per parlay");
        return cur;
      }
      return [...filtered, leg];
    });
  };

  const removeLeg = (leg) => setSlip((cur) => cur.filter((l) => legKey(l) !== legKey(leg)));
  const clearSlip = () => { setSlip([]); setPreview(null); };

  const compute = useCallback(async () => {
    if (slip.length < 2) { toast.error("Add at least 2 legs"); return; }
    setComputing(true);
    try {
      const { data } = await api.post("/parlay/compute", { legs: slip });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Compute failed");
    } finally { setComputing(false); }
  }, [slip]);

  const save = async () => {
    if (!preview) { toast.error("Compute first"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/parlay/save", { legs: slip, stake });
      toast.success(`Parlay tracked. Potential payout ${fmtMoney(data.potential_payout)}`);
      clearSlip();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="parlay-page">
      <PageHeader
        title="Parlay builder"
        subtitle="Stack legs from any upcoming game. Same-game correlation is captured via shared Monte Carlo draws."
        actions={slip.length > 0 && (
          <button onClick={clearSlip} className="iso-btn-ghost !py-1.5 !text-xs" data-testid="clear-slip-btn">
            <RotateCcw className="size-3.5" /> Clear slip
          </button>
        )}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr,380px]">
        {/* LEFT: game cards */}
        <div className="space-y-4">
          {games.length === 0 && (
            <div className="iso-card p-8 text-center text-sm text-gray-500">
              No upcoming games. Try refreshing live data.
            </div>
          )}
          {games.map((g) => (
            <GameCard key={g.id} game={g} selectedSet={selectedSet} onToggle={toggleLeg} />
          ))}
        </div>

        {/* RIGHT: sticky slip */}
        <div className="lg:sticky lg:top-24 h-fit">
          <SlipPanel
            slip={slip}
            games={games}
            preview={preview}
            stake={stake}
            setStake={setStake}
            onRemove={removeLeg}
            onCompute={compute}
            onSave={save}
            computing={computing}
            saving={saving}
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Game card ----------
const GameCard = ({ game, selectedSet, onToggle }) => {
  return (
    <div className="iso-card p-5" data-testid={`parlay-game-${game.id}`}>
      <div className="flex items-center justify-between">
        <LeagueChip code={game.league} />
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Calendar className="size-3" />
          {new Date(game.start_time).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-[1fr,auto] items-center gap-4">
        <div className="flex items-center gap-3">
          <TeamLogo src={game.away_logo} abbr={game.away_team_abbr} size={32} />
          <div className="text-sm font-semibold text-gray-900">{game.away_team_name}</div>
          <span className="text-xs text-gray-400">@</span>
          <TeamLogo src={game.home_logo} abbr={game.home_team_abbr} size={32} />
          <div className="text-sm font-semibold text-gray-900">{game.home_team_name}</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {MARKETS.map((m) => {
          const awayLeg = { game_id: game.id, ...m.sideAway };
          const homeLeg = { game_id: game.id, ...m.sideHome };
          const awayOdds = oddsFor(game, m.sideAway);
          const homeOdds = oddsFor(game, m.sideHome);
          const awayLabel = m.labelAway ?? game.away_team_abbr;
          const homeLabel = m.labelHome ?? game.home_team_abbr;
          const awayLine = lineFor(game, m.sideAway);
          const homeLine = lineFor(game, m.sideHome);
          return (
            <div key={m.group} className="space-y-1.5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{m.group}</div>
              <LegButton
                leg={awayLeg} selected={selectedSet.has(legKey(awayLeg))}
                label={awayLabel} line={awayLine} odds={awayOdds} onClick={() => onToggle(awayLeg)}
                testid={`leg-${game.id}-${m.sideAway.market}-${m.sideAway.selection}`}
              />
              <LegButton
                leg={homeLeg} selected={selectedSet.has(legKey(homeLeg))}
                label={homeLabel} line={homeLine} odds={homeOdds} onClick={() => onToggle(homeLeg)}
                testid={`leg-${game.id}-${m.sideHome.market}-${m.sideHome.selection}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LegButton = ({ leg, selected, label, line, odds, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full rounded-lg border px-3 py-2 text-left transition-all ${
      selected
        ? "border-secondary bg-secondary-lighter/60 ring-2 ring-secondary/20"
        : "border-border bg-gray-0 hover:border-gray-400 hover:bg-gray-50"
    }`}
  >
    <div className="flex items-center justify-between">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-gray-900 truncate">{label}</div>
        {line != null && (
          <div className="text-[10px] font-medium text-gray-500">
            {line > 0 ? "+" : ""}{line}
          </div>
        )}
      </div>
      <div className="font-lexend text-sm font-bold text-gray-900">{fmtOdds(odds)}</div>
    </div>
  </button>
);

function oddsFor(game, pick) {
  const o = game.odds;
  if (pick.market === "moneyline") return pick.selection === "home" ? o.moneyline_home : o.moneyline_away;
  if (pick.market === "spread") return pick.selection === "home" ? o.spread_price_home : o.spread_price_away;
  if (pick.market === "total") return pick.selection === "over" ? o.total_over_price : o.total_under_price;
}

function lineFor(game, pick) {
  if (pick.market === "spread") return pick.selection === "home" ? game.odds.spread : -game.odds.spread;
  if (pick.market === "total") return game.odds.total;
  return null;
}

// ---------- Slip panel ----------
const SlipPanel = ({ slip, games, preview, stake, setStake, onRemove, onCompute, onSave, computing, saving }) => {
  const gamesById = useMemo(() => Object.fromEntries(games.map((g) => [g.id, g])), [games]);

  return (
    <div className="iso-card overflow-hidden" data-testid="parlay-slip">
      <div className="flex items-center justify-between border-b border-border bg-gray-50 px-5 py-3">
        <div className="flex items-center gap-2">
          <Layers3 className="size-4 text-secondary" />
          <div className="font-lexend text-sm font-bold text-gray-900">Bet slip</div>
        </div>
        <span className="iso-chip">{slip.length} leg{slip.length === 1 ? "" : "s"}</span>
      </div>

      {slip.length === 0 && (
        <div className="p-8 text-center">
          <Sparkles className="mx-auto size-8 text-gray-300" />
          <p className="mt-2 text-xs text-gray-500">Click legs on the left to build your parlay. Add 2+ to unlock the model.</p>
        </div>
      )}

      {slip.length > 0 && (
        <>
          <div className="max-h-[340px] overflow-y-auto custom-scrollbar">
            {slip.map((leg) => {
              const g = gamesById[leg.game_id];
              if (!g) return null;
              const label = legLabel(leg, g);
              const odds = oddsFor(g, leg);
              return (
                <div key={legKey(leg)} className="flex items-start gap-2 border-b border-border px-5 py-2.5 last:border-b-0" data-testid={`slip-leg-${legKey(leg)}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-gray-900 truncate">{label}</div>
                    <div className="text-[10px] text-gray-500">{g.away_team_abbr} @ {g.home_team_abbr} · {g.league}</div>
                  </div>
                  <div className="font-lexend text-xs font-bold text-gray-800 whitespace-nowrap">{fmtOdds(odds)}</div>
                  <button onClick={() => onRemove(leg)} className="text-gray-400 hover:text-red" data-testid={`remove-leg-${legKey(leg)}`}>
                    <X className="size-3.5" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="space-y-3 p-5">
            {!preview && (
              <button onClick={onCompute} disabled={computing || slip.length < 2} className="iso-btn-primary w-full" data-testid="compute-parlay-btn">
                {computing ? <Loader2 className="size-4 animate-spin" /> : <TrendingUp className="size-4" />}
                Compute edge
              </button>
            )}

            {preview && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 rounded-xl border border-border bg-gray-50 p-3 text-center">
                  <Stat label="Odds" value={preview.combined_american > 0 ? `+${preview.combined_american}` : preview.combined_american} />
                  <Stat label="Joint prob" value={fmtPct(preview.combined_prob, 1)} />
                  <Stat label="EV" value={`${preview.ev_percent >= 0 ? "+" : ""}${preview.ev_percent}%`} tone={preview.ev_percent >= 0 ? "green" : "red"} />
                </div>

                {preview.kelly > 0 && (
                  <div className="rounded-lg bg-secondary-lighter/50 px-3 py-2 text-xs text-secondary-dark">
                    <strong>Kelly suggests:</strong> {(preview.kelly * 100).toFixed(1)}% of bankroll
                  </div>
                )}

                <div>
                  <label className="iso-label">Stake ($)</label>
                  <input
                    type="number" min="1" value={stake}
                    onChange={(e) => setStake(Number(e.target.value) || 0)}
                    className="iso-input" data-testid="parlay-stake-input"
                  />
                  <div className="mt-1.5 flex justify-between text-xs text-gray-500">
                    <span>To win:</span>
                    <span className="font-lexend font-bold text-gray-900">
                      {fmtMoney(Math.round(stake * (preview.combined_decimal - 1) * 100) / 100)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button onClick={onCompute} disabled={computing} className="iso-btn-outline flex-1" data-testid="recompute-btn">
                    {computing ? <Loader2 className="size-4 animate-spin" /> : <TrendingUp className="size-4" />}
                    Recompute
                  </button>
                  <button onClick={onSave} disabled={saving || !stake} className="iso-btn-primary flex-1" data-testid="save-parlay-btn">
                    {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                    Track
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone = "default" }) => {
  const toneClass = tone === "green" ? "text-green-dark" : tone === "red" ? "text-red-dark" : "text-gray-900";
  return (
    <div>
      <div className="iso-label !mb-0.5">{label}</div>
      <div className={`font-lexend text-sm font-bold ${toneClass}`}>{value}</div>
    </div>
  );
};

function legLabel(leg, game) {
  if (leg.market === "moneyline") {
    const abbr = leg.selection === "home" ? game.home_team_abbr : game.away_team_abbr;
    return `${abbr} ML`;
  }
  if (leg.market === "spread") {
    const abbr = leg.selection === "home" ? game.home_team_abbr : game.away_team_abbr;
    const line = leg.selection === "home" ? game.odds.spread : -game.odds.spread;
    return `${abbr} ${line > 0 ? "+" : ""}${line}`;
  }
  if (leg.market === "total") {
    return `${leg.selection === "over" ? "Over" : "Under"} ${game.odds.total}`;
  }
  return "";
}
